import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { useIsMutating } from '@tanstack/react-query';
import { useSession, useSyncThemeFromProfile } from '@/auth/session';
import { useMe } from '@/api/hooks/core';
import { COPY_SHARE_KEY, useCopyShare, useSharedStrategy } from '@/api/hooks/lists';
import { messageOf } from '@/api/errors';
import { useGates } from '@/app/gates';
import { breakpoints } from '@/theme/tokens';
import { useMediaQuery } from '@/lib/hooks';
import { forgetShare, pendingShare, shareCodeFromPath } from '@/lib/pendingShare';
import { SignInPage } from '@/features/auth/SignInPage';
import { ShareInvitePage } from '@/features/share/ShareInvitePage';
import { OnboardingFlow } from '@/features/onboarding/OnboardingFlow';
import { useOnboardingSeen } from '@/features/onboarding/seen';
import { LoadingArea, toast } from '@/components/ui';
import { UpgradePrompt } from '@/components/domain/UpgradePrompt';
import { useStrategy } from '@/api/hooks/strategies';
import { CountryStep, LegalGate, NarrowGate, PhoneGate, PhoneSignedInGate, Splash, UpdateRequired, VerifyEmailStep } from './Gates';
import { Rail } from './Rail';
import { usePushBootstrap } from './usePush';
import s from './AppShell.module.css';

const CONTINUE_KEY = 'sixone8.continueOnPhone';
const CONTINUE_NARROW_KEY = 'sixone8.continueNarrow';
/** The signed-in shell's minimum width (`.shell` in AppShell.module.css); narrower is clipped. */
const SHELL_MIN_WIDTH = 1024;

function readFlag(key: string) {
  try {
    return sessionStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function setFlag(key: string) {
  try {
    sessionStorage.setItem(key, '1');
  } catch {
    // Non-fatal.
  }
}

/**
 * The root: decides between the blocking screens and the signed-in app. Order matters — an
 * outdated build blocks everything, then the phone-width redirect, then sign-in (with its email
 * verification and country steps), then legal, then the guided first-run.
 *
 * **A share link signs in on its own page** (features/share): someone signed out at `/s/<code>`
 * sees what was shared beside a sign-up panel, and its verification step says the strategy opens
 * straight after. Either way the URL is kept, so the signed-in app resumes on the link.
 */
export function RootLayout() {
  const { status } = useSession();
  const { updateRequired, legalRequired } = useGates();
  const narrow = useMediaQuery(`(max-width: ${breakpoints.compact - 1}px)`);
  const [continued, setContinued] = useState(() => readFlag(CONTINUE_KEY));
  const path = useRouterState({ select: (st) => st.location.pathname });

  if (updateRequired) return <UpdateRequired />;
  // Shared-strategy links never land on the phone screen (§2).
  if (narrow && !continued && !path.startsWith('/s/')) {
    return (
      <PhoneGate
        onContinue={() => {
          setFlag(CONTINUE_KEY);
          setContinued(true);
        }}
      />
    );
  }
  if (status === 'loading') return <Splash />;
  const shareCode = shareCodeFromPath(path);
  if (status === 'signedOut' && shareCode) return <ShareInvitePage code={shareCode} />;
  if (status === 'signedOut' || status === 'unconfigured') return <SignInPage />;
  if (status === 'needsVerification') return shareCode ? <ShareInvitePage code={shareCode} verify /> : <VerifyEmailStep />;
  if (status === 'needsCountry') return <CountryStep />;
  if (legalRequired) return <LegalGate />;
  // The admin dashboard brings its own shell (features/admin/AdminShell) instead of the product rail.
  if (path === '/admin' || path.startsWith('/admin/')) return <Outlet />;
  return (
    <FirstRun>
      <SignedIn>
        <Outlet />
      </SignedIn>
    </FirstRun>
  );
}

/**
 * The guided first-run (features/onboarding), while the profile says setup is owed and this browser
 * has not finished or skipped it. **Latched open once shown:** creating the pair flips
 * `setup_needed` on the next profile read, and the done step must survive that until the user
 * leaves it. The URL is left alone underneath, so a link the user arrived on (a shared strategy)
 * opens when the flow closes — the phone's "setup wins, the link resumes after".
 *
 * **Arriving on a share link changes the flow, not just what follows it.** A share the plan covers
 * replaces the template step — the strategy they were sent becomes the first strategy, and the
 * done step opens it. One it does not cover runs the ordinary flow, and the share page then saves
 * it suspended (`lib/pendingShare.ts`). So the share is fetched before the flow is shown.
 */
function FirstRun({ children }: { children: ReactNode }) {
  const me = useMe().data;
  const navigate = useNavigate();
  const path = useRouterState({ select: (st) => st.location.pathname });
  const [seen, markSeen] = useOnboardingSeen(me?.id);
  const owed = Boolean(me?.setup_needed) && !seen;
  const [open, setOpen] = useState(false);
  const shareCode = shareCodeFromPath(path);
  const shared = useSharedStrategy(shareCode ?? '', { enabled: (owed || open) && shareCode !== null });
  if (owed && !open) setOpen(true);
  if (!open && !owed) return children;
  if (shareCode && shared.isLoading) return <LoadingArea />;
  const home = path === '/' || path === '/today';
  return (
    <OnboardingFlow
      cryptoOnly={me?.entitlements.asset_scope === 'crypto'}
      share={shared.data ?? null}
      doneLabel={home ? 'Go to Today' : 'Continue'}
      onLeave={(next) => {
        markSeen();
        setOpen(false);
        if (next?.toAccount) void navigate({ to: '/account' });
        else if (next?.strategyId) void navigate({ to: '/strategies/$strategyId', params: { strategyId: next.strategyId } });
        else if (home) void navigate({ to: '/today' });
      }}
    />
  );
}

/**
 * Saves the share someone signed up from, wherever the app opened afterwards — an invite-code
 * detour to Account, a reload on Today. On the share's own page that page does it (and opens the
 * result), so this stands aside there. Cleared before the request, so it is tried once.
 */
function usePendingShareSave() {
  const path = useRouterState({ select: (st) => st.location.pathname });
  const { mutate } = useCopyShare();
  const started = useRef(false);
  useEffect(() => {
    const code = pendingShare();
    if (!code || started.current || shareCodeFromPath(path) === code) return;
    started.current = true;
    forgetShare();
    mutate(code, {
      onSuccess: (st) => toast.success(`${st.name} is saved to your Strategies`),
      onError: (e) => toast.error(`The strategy you were sent couldn't be saved: ${messageOf(e)}`),
    });
  }, [path, mutate]);
}

/**
 * Below the shell's minimum width (a tablet held upright, a narrow window) the app is covered by a
 * prompt to turn the device or widen the window (web_app_design.md §3). It covers the app rather
 * than replacing it, so turning a tablet mid-edit loses nothing.
 */
function SignedIn({ children }: { children: ReactNode }) {
  const me = useMe();
  useSyncThemeFromProfile(me.data?.theme_preference);
  usePushBootstrap();
  usePendingShareSave();
  const tooNarrow = useMediaQuery(`(max-width: ${SHELL_MIN_WIDTH - 1}px)`);
  const [continued, setContinued] = useState(() => readFlag(CONTINUE_NARROW_KEY));
  const covered = tooNarrow && !continued;
  // A phone held upright is past the phone screen only by way of a share link, so it gets the screen
  // that says the strategy is saved and sends its link to a computer, not the tablet prompt.
  const phone = useMediaQuery(`(max-width: ${breakpoints.compact - 1}px) and (pointer: coarse)`);
  const path = useRouterState({ select: (st) => st.location.pathname });
  const strategyId = /^\/strategies\/([^/]+)$/.exec(path)?.[1];
  const strategy = useStrategy(covered && phone ? strategyId : undefined).data;
  // A share opened by an existing account is not saved for them (only a sign-up's is): offer it here.
  const shareCode = shareCodeFromPath(path);
  const copyShare = useCopyShare();
  // The share page saves a sign-up's share on arrival; while any save runs, offering one would copy twice.
  const savingElsewhere = useIsMutating({ mutationKey: COPY_SHARE_KEY }) - (copyShare.isPending ? 1 : 0) > 0;
  const navigate = useNavigate();
  const saveShare =
    covered && phone && shareCode && pendingShare() !== shareCode && !savingElsewhere
      ? () =>
          copyShare.mutate(shareCode, {
            onSuccess: (st) => void navigate({ to: '/strategies/$strategyId', params: { strategyId: st.id } }),
            onError: (e) => toast.error(messageOf(e)),
          })
      : undefined;
  const onContinue = () => {
    setFlag(CONTINUE_NARROW_KEY);
    setContinued(true);
  };
  return (
    <>
      <div className={covered ? `${s.shell} ${s.covered}` : s.shell} inert={covered}>
        <Rail />
        <main className={s.main}>{children}</main>
      </div>
      <UpgradePrompt />
      {covered &&
        (phone ? (
          <PhoneSignedInGate
            strategyName={strategy?.name}
            email={me.data?.email ?? undefined}
            onSave={saveShare}
            saving={copyShare.isPending}
            onContinue={onContinue}
          />
        ) : (
          <NarrowGate onContinue={onContinue} />
        ))}
    </>
  );
}
